import type { Dayjs, OpUnitType } from "dayjs";
import dayjs from "dayjs";
import { cloneDeep, find, forEach, get, includes, isEmpty, isUndefined } from "lodash";

import type { ICategories } from "app/common/services/categoryService.types";
import { swSettings } from "app/common/services/swSettings";
import type { ICampaignsScrapingConfiguration } from "app/components/campaigns/shared/CampaignsConst";
import { getScarpingConfigsForCurrentDevice } from "app/components/campaigns/shared/CampaignsUtils";
import { EFamilyTypes } from "app/components/dashboard/dashboard-templates/components/DashboardTemplatesTypes";
import widgetSettings from "app/components/dashboard/WidgetSettings";
import type { IWidgetIndustryKey } from "app/components/widget/widget-types/Widget";
import { translate } from "app/filters/i18nFilter";
import { DeviceService } from "app/pages/app-intelligence/common-components/device-picker/DeviceService";
import { EDevice } from "app/pages/app-intelligence/common-components/device-picker/types";
import { AppStoreID, AppStoreName } from "app/pages/app-intelligence/constants";
import { getAvailableFilters } from "app/pages/app-intelligence/utils";
import { UserCustomCategoryService } from "app/services/category/userCustomCategoryService";
import { BasicDurations } from "app/services/DurationService";
import { periodOverPeriodService } from "app/services/PeriodOverPeriodService";
import { fetchSiteInfoQuery } from "app/services/sitesService/queries/sitesQueries";

import { SEGMENT_ID, WEBSITE_ID } from "../components/segments/constants";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

interface IDatePickerPresets {
    displayText: string;
    buttonText: string;
    value: string;
    enabled: boolean;
    locked: boolean;
}
const BUTTON_TEXT_PREFIX = "Last";

export const isMonthByMonthPresets = (componentId: string): boolean =>
    swSettings?.components?.[componentId]?.resources?.FixedSnapshotRangeLength === 1;

export const getButtonText = (currentButtonText: string, isMonthByMonthPresetDuration: boolean): string =>
    isMonthByMonthPresetDuration || currentButtonText?.startsWith(BUTTON_TEXT_PREFIX)
        ? currentButtonText
        : `${BUTTON_TEXT_PREFIX} ${currentButtonText}`;

export const shouldDisableButton = (value: string, disableWindowInDatepicker: boolean): boolean =>
    disableWindowInDatepicker && value === BasicDurations.LAST_TWENTY_EIGHT_DAYS;

function dateFromString(yearMonthString) {
    const date = yearMonthString.split(".");
    return dayjs
        .utc()
        .year(parseInt(date[0]))
        .month(parseInt(date[1]) - 1);
}

export function isAllowedDurationInComponent(component, duration, family) {
    const customDuration = duration.split("-");
    let granularity: OpUnitType, start: Dayjs, end: Dayjs;
    let returnedValue;

    if (family === EFamilyTypes.RankTracker) {
        return true;
    }

    if (component.CustomDurations) {
        return component.CustomDurations.indexOf(duration) > -1;
    }

    // custom duration
    if (customDuration.length > 1) {
        (granularity = "month"),
            (start = dateFromString(customDuration[0]).startOf(granularity).clone()),
            (end = dateFromString(customDuration[1]).endOf(granularity).clone());

        returnedValue =
            (start.isAfter(component.minDate, granularity) || start.isSame(component.minDate, granularity)) &&
            (end.isBefore(component.maxDate, granularity) || end.isSame(component.maxDate, granularity));
    }
    // preset
    else {
        const preset: any = find(component.datePickerPresets, { value: duration });
        returnedValue = !isUndefined(preset) ? preset.enabled && !preset.locked : false;
    }
    return returnedValue;
}

export function isMetricAllowsTrafficSource(metricProps, trafficSource) {
    if (metricProps.onlyTotal) {
        return trafficSource === devicesTypes.TOTAL;
    }
    const hasWebSource =
        metricProps.component === "IndustryAnalysisOverview"
            ? metricProps.modules.Industry.hasWebSource || metricProps.hasWebSource
            : metricProps.hasWebSource;
    const viewPermissions =
        metricProps.component === "IndustryAnalysisOverview"
            ? metricProps.modules.Industry.viewPermissions || metricProps.viewPermissions
            : metricProps.viewPermissions;

    if (trafficSource == getComponentDefaultWebSource(metricProps.component)) {
        return true;
    }
    if (hasWebSource && !viewPermissions) {
        return true;
    } else if (!hasWebSource && !viewPermissions) {
        return false;
    }
    return viewPermissions && viewPermissions.trafficSources && includes(viewPermissions.trafficSources, trafficSource);
}

function getComponentDefaultWebSource(componentId) {
    return swSettings.components[componentId].resources.DefaultSource;
}

export function getComponentProperties(component, metricProps, key?) {
    const _component = swSettings.components[component];
    let countriesList = _component.allowedCountries;
    if (
        metricProps?.family === "Mobile" &&
        (_component.resources.CountriesAndroid || _component.resources.CountriesIOS)
    ) {
        if (key.length) {
            const isIOS = key[0].store === AppStoreName.APPLE.toLowerCase();
            const useIOSCountries = isIOS && !!_component.resources.CountriesIOS;
            const useAndroidCountries = !isIOS && !!_component.resources.CountriesAndroid;
            countriesList = useIOSCountries
                ? _component.allowedIOSCountries
                : useAndroidCountries
                  ? _component.allowedAndroidCountries
                  : _component.allowedCountries;
        }
    }
    const allowedCountries = [];
    countriesList.forEach((country: any, index) => {
        if (!country.children || country.children.length < 1) {
            allowedCountries.push({
                ...country,
            });
        } else {
            allowedCountries.push({
                ...country,
            });
            country.children.forEach((state, index) => {
                allowedCountries.push({
                    ...state,
                });
            });
        }
    });
    const isMonthByMonthPresetDuration = isMonthByMonthPresets(component);
    const datePickerPresetsDeepCopy = cloneDeep(_component.datePickerPresets);
    const presetDate = (preset: IDatePickerPresets) => {
        const textToDisplay = translate("datepicker.lastn", {
            count: "1",
            unit: translate("datepicker.units.month.single"),
        });
        preset.displayText = textToDisplay;
        preset.buttonText = textToDisplay;
        preset.value = BasicDurations.LAST_MONTH;
        if (datePickerPresets?.[0]?.value === BasicDurations.LAST_TWENTY_EIGHT_DAYS) {
            const firstItem = datePickerPresets.shift();
            datePickerPresets.unshift(preset);
            datePickerPresets.unshift(firstItem);
        } else {
            datePickerPresets.unshift(preset);
        }
        return datePickerPresets;
    };
    const datePickerPresets = datePickerPresetsDeepCopy.map((preset) => {
        const elem: any = Object.assign({}, preset);
        elem.buttonText = getButtonText(elem.buttonText, isMonthByMonthPresetDuration);
        if (shouldDisableButton(elem.value, metricProps?.disableWindowInDatepicker)) {
            elem.enabled = false;
        }
        return elem;
    });
    // if the component is month by month, we need to add the last month preset
    // to the beginning of the presets list and change the text to "Last 1 month"
    // unless the first preset is "Last 28 days"
    let preset;
    if (isMonthByMonthPresetDuration && datePickerPresets?.[0]?.value === BasicDurations.LAST_TWENTY_EIGHT_DAYS) {
        const firstItem = datePickerPresets.shift();
        preset = datePickerPresets.shift();
        datePickerPresets.unshift(firstItem);

        presetDate(preset);
    } else if (
        isMonthByMonthPresetDuration &&
        datePickerPresets?.[0]?.value !== BasicDurations.LAST_TWENTY_EIGHT_DAYS
    ) {
        preset = datePickerPresets.shift();
        presetDate(preset);
    }

    return {
        countries: allowedCountries,
        datePickerPresets,
        minDate: _component.startDate,
        maxDate: _component.endDate,
        defaultParams: {
            ..._component.defaultParams,
            ...(metricProps?.defaultDuration ? { duration: metricProps?.defaultDuration } : {}),
        },
        customDurations: _component.resources.CustomDurations,
    };
}

export function getComponentByWebSource(customDashboardState, webSource, metric?, family?, key?) {
    const _metric = metric || customDashboardState.widget.metric;
    const _family = family || customDashboardState.widget.family;
    const _metricProperties = widgetSettings.getMetricProperties(_metric, _family);
    if (webSource === "Total" && _metricProperties.totalComponent) {
        return _metricProperties.totalComponent;
    } else if (webSource === "MobileWeb") {
        return _metricProperties.mobileWebComponent || _metricProperties.component;
    } else if (_metricProperties.componentFunction) {
        return _metricProperties.componentFunction({ webSource, metric, family, key });
    }
    return _metricProperties.component;
}

export function getMetricProperties(metric, family) {
    return widgetSettings.getMetricProperties(metric, family);
}

export function getProductClaims() {
    return swSettings.components;
}

export function getMetricWidgetTypes(
    metric: string,
    isCompare: boolean,
    customDashboardState: any,
    compareOnly: boolean,
    newKey?: string,
) {
    const _metricGetDashboardWidgetTypes = widgetSettings.getMetric(metric).getDashboardWidgetTypes;
    const isCompareMode = isCompare || compareOnly;
    const _widgetTypes: any = widgetSettings.getMetricWidgets(metric, isCompareMode);
    const _defaultWidgetTypes: any = getDefaultWidgetTypes();

    if (typeof _metricGetDashboardWidgetTypes === "function") {
        const _customMetricWidgetTypes = _metricGetDashboardWidgetTypes(customDashboardState, isCompare, newKey);
        _customMetricWidgetTypes.forEach((type) => {
            const _dashboardType = widgetSettings.getWidgetDashboardType(type) || type;
            const _defaultType: any = find(_defaultWidgetTypes, { id: _dashboardType });
            if (_defaultType) {
                _defaultType.disabled = false;
                _defaultType.type = type;
            }
        });
    } else {
        for (const type in _widgetTypes) {
            const _dashboardType = widgetSettings.getWidgetDashboardType(type) || type;
            const _defaultType: any = find(_defaultWidgetTypes, { id: _dashboardType });
            const _isDefaultType = !isEmpty(find(_defaultWidgetTypes, { id: type }));
            if (!(_widgetTypes[_dashboardType] && !_isDefaultType)) {
                if (_defaultType) {
                    _defaultType.disabled = false;
                    _defaultType.type = type;
                }
            }
        }
    }

    const _result = _defaultWidgetTypes.map((type) => {
        if (
            metric === "EngagementVisits" &&
            !isCompare &&
            type.id === "PieChart" &&
            customDashboardState.widget.webSource !== "Total"
        ) {
            type.disabled = true;
        }
        return {
            id: type.id,
            type: type.type,
            order: type.order,
            text: type.title,
            disabled: type.disabled,
        };
    });

    return _result;
}

function getDefaultWidgetTypes() {
    const defaultWidgetTypes = forEach(widgetSettings.getWidgetTypes(), (widgetType) => {
        widgetType.disabled = true;
    });
    return defaultWidgetTypes;
}

export function getWebsiteHeaderData(keys: { type?: string; name?: string; segmentName?: string; domain?: string }[]) {
    const transformedKeys: string = keys
        .map((key) => {
            if (key.type === WEBSITE_ID) {
                return key?.domain;
            }
            if (key.type === SEGMENT_ID) {
                return key?.segmentName;
            }
            return key?.name;
        })
        .join();

    return fetchSiteInfoQuery({
        keys: transformedKeys,
        includeCrossData: true,
        mainDomainOnly: true,
    });
}

export function getMetricWidgetFilters(metric, family, type, isCompare, customDashboardState, compareOnly) {
    const availableWidgetTypes = getMetricWidgetTypes(metric, isCompare, customDashboardState, compareOnly);
    if (typeof type === "object") {
        type = type.type;
    }

    const currentSelectedWidgetType: any = find(availableWidgetTypes, {
        type: type,
        disabled: false,
    });
    if (currentSelectedWidgetType) {
        type = currentSelectedWidgetType.type;
    } else {
        const firstTypeAvailable: any = find(availableWidgetTypes, { disabled: false });
        type = firstTypeAvailable.type;
    }

    if (type == "ComparedLine" && !customDashboardState.widget.comparedDuration) {
        type = "Graph";
    }
    const availableFilters = widgetSettings.getMetricWidgetFilters(metric, type, isCompare);
    if (customDashboardState.widget.comparedDuration) {
        if (availableFilters["timeGranularity"]) {
            delete availableFilters["timeGranularity"];
        }
    }

    if (
        family === "Website" &&
        ["WebDemographicsAge", "WebDemographicsGender", "MarketShares"].indexOf(metric) === -1
    ) {
        availableFilters["includeSubDomains"] = [
            {
                id: "true",
                text: "Yes",
            },
            {
                id: "false",
                text: "No",
            },
        ];
    }

    if (family === "Industry") {
        if (availableFilters.hasOwnProperty("ShouldGetVerifiedData")) {
            delete availableFilters["ShouldGetVerifiedData"];
        }
    }

    return availableFilters;
}

export function getPeriodOverPeriodParams(family, metric, key, duration, component) {
    // PoP is allowed only if the metric supports it and the user have permission for it
    if (family === EFamilyTypes.RankTracker) {
        return {
            enabled: false,
            items: [],
        };
    }
    const metricHasPeriodOverPeriod = periodOverPeriodService.periodOverPeriodEnabledForMetric(
        metric,
        key.length > 1,
        family,
    );
    const comparedDurationItems = periodOverPeriodService.getPeriodOverPeriodDropdownItems(duration, key, component);
    return {
        enabled: metricHasPeriodOverPeriod,
        items: comparedDurationItems,
    };
}

export function getFormElementsStatus(formElements, action, widget) {
    const _family = action.family || widget.family;
    const _metric = action.metric || widget.metric;
    const _isCompare = action.isCompare !== undefined ? action.isCompare : widget.key.length > 1;
    Object.keys(formElements).forEach((element) => {
        switch (element) {
            case "webSource":
                if (_family === "Mobile") {
                    formElements[element].isActive = false;
                } else {
                    formElements[element].isActive = true;
                }
                break;
            case "trafficChannels":
                if (action.metricProps.multipleChannelSupport && _isCompare) {
                    formElements[element].isActive = true;
                } else {
                    formElements[element].isActive = false;
                }
                break;
            case "device":
                formElements[element].isActive =
                    _family === "Mobile" && !action?.metricProps?.options?.deviceFilteringUnavailable;
                break;
            default:
                formElements[element].isActive = true;
        }
    });
    return formElements;
}

export function getWidgetWidth(widgetType) {
    if (typeof widgetType === "string") {
        return widgetSettings.getDefaultWidgetSize(widgetType);
    } else {
        return widgetSettings.getDefaultWidgetSize(widgetType.type);
    }
}

export function isMetricMetaDataNeeded(metric, family) {
    const _metricProps = getMetricProperties(metric, family);
    return _metricProps.dynamicSettings;
}

export const getRelevantDevicesList = (
    store: string,
    keys: string[],
    navigationState: string,
    deviceParam?: EDevice,
) => {
    const isIos = store?.toLowerCase() === AppStoreName.APPLE.toLowerCase();
    const params = { appId: `${isIos ? AppStoreID.APPLE : AppStoreID.GOOGLE}_${keys.join(",")}` };
    const { device: deviceFilterConfig } = getAvailableFilters(navigationState, params);
    const useCombinedDevices =
        deviceParam?.toLowerCase() === EDevice.ALL_DEVICES.toLowerCase() ? false : keys?.length === 1;

    return isIos
        ? (deviceFilterConfig?.iosDevices ?? DeviceService.getIosDevices(useCombinedDevices))
        : (deviceFilterConfig?.androidDevices ?? DeviceService.getAndroidDevices(useCombinedDevices));
};

export const getSegmentIds = (newWidget) => {
    const segmentIdFromKeys = newWidget.key.map((item) => item.id);

    if (segmentIdFromKeys[0]) {
        return segmentIdFromKeys[0];
    } else {
        return newWidget?.filters?.segmentId;
    }
};

export const getScrapingConfsIDsAsString = (
    scrapingConfigurations: ICampaignsScrapingConfiguration[],
    webSource: string,
): string => {
    return getScarpingConfigsForCurrentDevice(scrapingConfigurations, webSource)
        .map((sc) => sc.id)
        .join(",");
};

export const getCustomIndustryIdFromModel = (industryList?: ICategories | IWidgetIndustryKey[]): string | undefined => {
    const industryID =
        get(industryList, "[0].customIndustryId", undefined) || get(industryList, "[0].category", undefined);

    const isCustomId = !!UserCustomCategoryService.getCustomAndSharedCategoryById(industryID);

    return isCustomId ? industryID : undefined;
};
